import type { CardUid } from '../../core/ids';

/**
 * **手牌顺序回写** —— 把"扇形里的视觉顺序"翻译成一串 `hand/reorder`。
 *
 * 纯函数、不碰 Pixi、不碰状态，所以热座那条最容易错的路能在这里钉住。
 *
 * ## 三个顺序，别混
 *
 * | 名字 | 是什么 |
 * | --- | --- |
 * | `fed` | **喂给手牌模块时**那一手的 uid（状态里的顺序） |
 * | `order` | 手牌模块自己的位次表：`order[i]` = 第 i 位摆的是喂入表里的第几张 |
 * | `live` | **现在状态里**那一手的 uid（可能已经被上一次回写改过） |
 *
 * 早先的实现拿 `fed` 当基准算位移：回写之后状态顺序变了，而位次表没变，
 * 于是**每帧又挪一次**（还因为回写的判据被一个守卫挡住，正常回合里干脆
 * 从不回写 —— 顺序在下一次重新喂牌时被打回原样）。用 `live` 当基准，
 * 回写完成的那一刻目标就是恒等置换，自然幂等。
 */

/** 把 `current` 排成 `target` 所需的 `[from, to]` 序列（reducer 的 splice 语义） */
export function movesToOrder(
  current: readonly unknown[],
  target: readonly number[],
): Array<[number, number]> {
  const work = current.map((_value, i) => i);
  const moves: Array<[number, number]> = [];
  for (let i = 0; i < target.length; i++) {
    const want = target[i];
    if (want === undefined) continue;
    const at = work.indexOf(want);
    if (at < 0 || at === i) continue;
    const [moved] = work.splice(at, 1);
    if (moved === undefined) continue;
    work.splice(i, 0, moved);
    moves.push([at, i]);
  }
  return moves;
}

/**
 * **这一帧该发哪几条 `hand/reorder`。**
 *
 * 返回：
 * - `null`：现在不能回写（手牌正在换人 / 换牌，或喂入表和当前状态不是同一手）；
 * - `[]`：视觉顺序已经和状态一致，什么都不用做（**幂等**就靠这一条）；
 * - 否则：依次发给 reducer 的移动序列。
 */
export function handOrderMoves(
  fed: readonly CardUid[],
  order: readonly number[],
  live: readonly CardUid[],
): Array<[number, number]> | null {
  if (order.length !== fed.length) return null;
  if (live.length !== fed.length) return null;
  const liveSet = new Set(live);
  if (!fed.every((uid) => liveSet.has(uid))) return null;

  const visual: CardUid[] = [];
  for (const i of order) {
    const uid = fed[i];
    if (uid === undefined) return null;
    visual.push(uid);
  }
  const target = visual.map((uid) => live.indexOf(uid));
  if (target.some((i) => i < 0)) return null;
  if (target.every((v, i) => v === i)) return [];
  return movesToOrder(live, target);
}
